import React, { useEffect, useMemo, useState } from 'react';
import { IndianRupee, Loader2, Info, Lock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import api from '@/utils/api';
import { useUser } from '@/contexts/UserContext';

interface Money { charge: number; with_gst: number; }
interface CategoryRow extends Money { category: string; paid_messages: number; free_messages: number; rate: number; }
interface SplitRow extends Money { key: string; messages: number; }
interface DayRow { day: string; total: number; [category: string]: number | string; }
interface Billing {
  currency: string; gst_rate: number; from_date: string; to_date: string; meta_fetched_at: string;
  totals: Money & { paid_messages: number; free_messages: number; avg_per_paid_message: number };
  by_category: CategoryRow[];
  by_day: DayRow[];
  by_template: SplitRow[];
  by_agent: SplitRow[];
  attribution: {
    matched_messages: number; matched_charge: number; matched_with_gst: number;
    unmatched_messages: number; unmatched_charge: number; unmatched_with_gst: number;
    since: string | null;
  };
}

const CATEGORY_LABEL: Record<string, string> = {
  MARKETING: 'Marketing', UTILITY: 'Utility', AUTHENTICATION: 'Authentication', SERVICE: 'Service (customer chats)',
};
const CATEGORY_HINT: Record<string, string> = {
  MARKETING: 'Promotional templates — product cards, offers',
  UTILITY: 'Transactional templates — first message, payment/checkout links, order updates',
  AUTHENTICATION: 'OTP templates',
  SERVICE: 'Free-form replies inside the 24h window — free',
};
const CATEGORY_COLOR: Record<string, string> = {
  marketing: '#d97706', utility: '#153d23', authentication: '#2563eb',
};

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const presets = () => {
  const t = new Date();
  const monthStart = new Date(t.getFullYear(), t.getMonth(), 1);
  const lastStart = new Date(t.getFullYear(), t.getMonth() - 1, 1);
  const lastEnd = new Date(t.getFullYear(), t.getMonth(), 0);
  const back = (days: number) => { const d = new Date(t); d.setDate(d.getDate() - days + 1); return d; };
  return [
    { key: 'this_month', label: 'This month', from: iso(monthStart), to: iso(t) },
    { key: 'last_month', label: 'Last month', from: iso(lastStart), to: iso(lastEnd) },
    { key: '30d', label: 'Last 30 days', from: iso(back(30)), to: iso(t) },
    { key: '90d', label: 'Last 90 days', from: iso(back(90)), to: iso(t) },
    { key: 'year', label: 'This year', from: `${t.getFullYear()}-01-01`, to: iso(t) },
  ];
};

const inr = (v: number, digits = 2) =>
  `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const num = (v: number) => (v || 0).toLocaleString('en-IN');
const fmtDay = (s: string) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const Tile: React.FC<{ label: string; value: string; sub?: React.ReactNode; strong?: boolean }> = ({ label, value, sub, strong }) => (
  <div className={`bg-white rounded-lg border p-4 ${strong ? 'border-[#153d23]' : 'border-gray-200'}`}>
    <div className="text-xs font-medium text-gray-500 mb-1.5">{label}</div>
    <div className={`text-2xl font-bold ${strong ? 'text-[#153d23]' : 'text-gray-900'}`}>{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const SplitTable: React.FC<{ title: string; keyLabel: string; rows: SplitRow[]; total: number }> = ({ title, keyLabel, rows, total }) => (
  <div className="bg-white rounded-lg border border-gray-200">
    <div className="px-4 py-3 border-b font-semibold text-sm text-gray-900">{title}</div>
    {rows.length === 0 ? (
      <p className="text-sm text-gray-500 p-6 text-center">No paid messages matched in this range.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">{keyLabel}</th>
              <th className="text-right px-4 py-2 font-medium">Paid msgs</th>
              <th className="text-right px-4 py-2 font-medium">Meta charge</th>
              <th className="text-right px-4 py-2 font-medium">With GST</th>
              <th className="text-right px-4 py-2 font-medium">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-800 break-all">{r.key}</td>
                <td className="px-4 py-2 text-right text-gray-600">{num(r.messages)}</td>
                <td className="px-4 py-2 text-right text-gray-800">{inr(r.charge)}</td>
                <td className="px-4 py-2 text-right font-semibold text-gray-900">{inr(r.with_gst)}</td>
                <td className="px-4 py-2 text-right text-gray-500">{total ? `${((r.charge / total) * 100).toFixed(1)}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/**
 * WhatsApp Billing -- GET /whatsapp-cloud/billing. The amount is Meta's own pricing
 * analytics for our WABA; the template / agent split comes from our message logs,
 * each message priced at that day's actual Meta rate for its category. Super admins only.
 */
const WhatsAppBillingPage: React.FC = () => {
  const { userData } = useUser();
  const isSuperAdmin = (userData as { data?: { role?: { role_id?: number } } } | null)?.data?.role?.role_id === 9401;
  const ranges = useMemo(presets, []);
  const [rangeKey, setRangeKey] = useState('this_month');
  const [from, setFrom] = useState(ranges[0].from);
  const [to, setTo] = useState(ranges[0].to);
  const [data, setData] = useState<Billing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin || !from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.req({ url: `/whatsapp-cloud/billing?from_date=${from}&to_date=${to}`, method: api.Method.GET })
      .then((res) => {
        if (cancelled) return;
        const r = res as { success?: boolean; message?: string; data?: Billing } | null;
        if (r?.success && r.data) setData(r.data);
        else { setData(null); setError(r?.message || 'Could not load billing'); }
      })
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        setError((e as Error)?.message || 'Could not load billing');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, isSuperAdmin]);

  const pick = (k: string) => {
    const p = ranges.find((r) => r.key === k);
    if (!p) return;
    setRangeKey(k); setFrom(p.from); setTo(p.to);
  };

  const chartCats = useMemo(() => {
    const set = new Set<string>();
    data?.by_day.forEach((d) => Object.keys(d).forEach((k) => { if (k !== 'day' && k !== 'total') set.add(k); }));
    return Array.from(set);
  }, [data]);

  if (!isSuperAdmin) {
    return (
      <div className="w-full">
        <ModernSettingHeader title="WhatsApp Billing" description="What Meta has charged for WhatsApp" icon={<IndianRupee className="w-6 h-6" />} />
        <div className="p-6">
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Only super admins can see WhatsApp billing.</p>
          </div>
        </div>
      </div>
    );
  }

  const t = data?.totals;
  const a = data?.attribution;
  const gstPct = Math.round((data?.gst_rate ?? 0.18) * 100);

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="WhatsApp Billing"
        description="What Meta has charged our WhatsApp Business account, and which templates and agents it went on."
        icon={<IndianRupee className="w-6 h-6" />}
      />

      <div className="p-6 space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          {ranges.map((r) => (
            <button key={r.key} onClick={() => pick(r.key)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${rangeKey === r.key
                      ? 'border-brandColor text-brandColor bg-green-50 font-medium'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {r.label}
            </button>
          ))}
          <div className="flex items-center gap-1.5 ml-auto text-sm">
            <input type="date" value={from} max={to} onChange={(e) => { setRangeKey('custom'); setFrom(e.target.value); }}
                   className="h-8 border border-gray-200 rounded-lg px-2 text-sm" />
            <span className="text-gray-400">to</span>
            <input type="date" value={to} min={from} onChange={(e) => { setRangeKey('custom'); setTo(e.target.value); }}
                   className="h-8 border border-gray-200 rounded-lg px-2 text-sm" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-brandColor" />
            <p className="mt-4 text-gray-500">Reading billing from Meta…</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-white rounded-lg border border-red-200 text-sm text-red-600">{error}</div>
        ) : !data || !t || !a ? null : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <Tile strong label={`Total billed (incl. ${gstPct}% GST)`} value={inr(t.with_gst)} sub={`${fmtDay(data.from_date)} – ${fmtDay(data.to_date)}`} />
              <Tile label="Meta charge (before GST)" value={inr(t.charge)} sub={`GST ${inr(t.with_gst - t.charge)}`} />
              <Tile label="Paid messages" value={num(t.paid_messages)} sub={`avg ${inr(t.avg_per_paid_message, 3)} each`} />
              <Tile label="Free messages" value={num(t.free_messages)} sub="replies inside the 24h window, free entry points" />
              <Tile label="Average per day"
                    value={inr(t.with_gst / Math.max(data.by_day.length, 1))}
                    sub="incl. GST" />
            </div>

            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b font-semibold text-sm text-gray-900">By category</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Category</th>
                      <th className="text-right px-4 py-2 font-medium">Paid msgs</th>
                      <th className="text-right px-4 py-2 font-medium">Free msgs</th>
                      <th className="text-right px-4 py-2 font-medium">Rate / msg</th>
                      <th className="text-right px-4 py-2 font-medium">Meta charge</th>
                      <th className="text-right px-4 py-2 font-medium">With GST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_category.map((c) => (
                      <tr key={c.category} className="border-t border-gray-100">
                        <td className="px-4 py-2">
                          <div className="text-gray-900 font-medium">{CATEGORY_LABEL[c.category] || c.category}</div>
                          {CATEGORY_HINT[c.category] && <div className="text-[11px] text-gray-400">{CATEGORY_HINT[c.category]}</div>}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-600">{num(c.paid_messages)}</td>
                        <td className="px-4 py-2 text-right text-gray-600">{num(c.free_messages)}</td>
                        <td className="px-4 py-2 text-right text-gray-600">{c.rate ? inr(c.rate, 4) : '—'}</td>
                        <td className="px-4 py-2 text-right text-gray-800">{inr(c.charge)}</td>
                        <td className="px-4 py-2 text-right font-semibold text-gray-900">{inr(c.with_gst)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                      <td className="px-4 py-2 text-gray-900">Total</td>
                      <td className="px-4 py-2 text-right">{num(t.paid_messages)}</td>
                      <td className="px-4 py-2 text-right">{num(t.free_messages)}</td>
                      <td className="px-4 py-2 text-right">{inr(t.avg_per_paid_message, 4)}</td>
                      <td className="px-4 py-2 text-right">{inr(t.charge)}</td>
                      <td className="px-4 py-2 text-right">{inr(t.with_gst)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="font-semibold text-sm text-gray-900 mb-3">Daily Meta charge (before GST)</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_day} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} minTickGap={12} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `₹${v}`} width={50} />
                    <Tooltip labelFormatter={(l) => fmtDay(String(l))} formatter={(v: number, n: string) => [inr(v), CATEGORY_LABEL[n.toUpperCase()] || n]} />
                    <Legend formatter={(n: string) => CATEGORY_LABEL[n.toUpperCase()] || n} />
                    {chartCats.map((c) => (
                      <Bar key={c} dataKey={c} stackId="cost" fill={CATEGORY_COLOR[c] || '#9ca3af'} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <SplitTable title="By template" keyLabel="Template" rows={data.by_template} total={t.charge} />
              <SplitTable title="By agent (who sent it)" keyLabel="Agent" rows={data.by_agent} total={t.charge} />
            </div>

            <div className="flex gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <Info className="w-4 h-4 flex-shrink-0 text-gray-400 mt-0.5" />
              <div className="space-y-1">
                <p>
                  <b>Amounts are Meta&apos;s own billing figures</b> for our WhatsApp Business account (refreshed every 30 min;
                  read at {new Date(data.meta_fetched_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}).
                  GST is added at {gstPct}% as Meta invoices it; the invoice itself is in Meta Business Settings → Billing.
                </p>
                <p>
                  The template and agent split prices each paid message at that day&apos;s Meta rate for its category, so it
                  can differ from the total by a few paise.
                  {a.since && <> Our logs carry billing detail from {fmtDay(a.since)}; earlier sends are not split.</>}
                </p>
                {a.unmatched_messages > 0 && (
                  <p className="text-amber-700">
                    {num(a.unmatched_messages)} paid message(s), {inr(a.unmatched_with_gst)} incl. GST, don&apos;t match a message
                    sent from the CRM (e.g. sent from Meta Business Suite, or before {a.since ? fmtDay(a.since) : 'logging began'}).
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppBillingPage;
